import React, { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Alert, Button, Card, CardBody, Col, FormGroup, Input, Label, Row, Spinner, Table } from 'reactstrap'
import TitlePage from 'components/atoms/title-page'
import { ALERT_TYPES } from 'constants/alert-constants'
import InboundJourneyApiV2 from 'api/v2/admins/inbound-journey-api-v2'
import { formatPhase3Date, getPhase3ErrorMessage, readPhase3Text } from '../helpers/phase3-inbound-journey-helper'

const Phase3ConsignmentEditPage = ({ pageUtils, apiNamespace = 'all_team' }) => {
  const [ consignment, setConsignment ] = useState(null)
  const [ users, setUsers ] = useState([])
  const [ userDetails, setUserDetails ] = useState([])
  const [ userSearch, setUserSearch ] = useState('')
  const [ form, setForm ] = useState({ user_id: '', consignor_detail_id: '' })
  const [ isLoading, setIsLoading ] = useState(true)
  const [ isSaving, setIsSaving ] = useState(false)

  const { id } = useParams()

  const fetchUserDetails = useCallback(async (userId) => {
    if (!userId) {
      setUserDetails([])
      return []
    }

    try {
      const response = await InboundJourneyApiV2.getConsignmentUserDetails({ user_id: userId, apiNamespace })
      const details = response.data?.data || []
      setUserDetails(details)
      return details
    } catch (error) {
      pageUtils?.setApiErrorMsg?.(error.response?.data || getPhase3ErrorMessage(error))
      return []
    }
  }, [ apiNamespace, pageUtils ])

  const fetchConsignment = useCallback(async () => {
    setIsLoading(true)

    try {
      const response = await InboundJourneyApiV2.showConsignmentForEdit({ id, apiNamespace })
      const nextConsignment = response.data?.data
      const nextUsers = response.data?.options?.users || []
      const nextDetails = response.data?.options?.user_details || []

      setConsignment(nextConsignment)
      setUsers(nextUsers)
      setUserDetails(nextDetails)
      setForm({
        user_id             : nextConsignment?.consignor?.id?.toString() || '',
        consignor_detail_id : nextConsignment?.consignor_detail?.id?.toString() || ''
      })
    } catch (error) {
      pageUtils?.setApiErrorMsg?.(error.response?.data || getPhase3ErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }, [ apiNamespace, id, pageUtils ])

  useEffect(() => {
    fetchConsignment()
  }, [ fetchConsignment ])

  const searchUsers = async () => {
    try {
      const response = await InboundJourneyApiV2.searchConsignmentUsers({ q: userSearch, apiNamespace })
      setUsers(response.data?.data || [])
    } catch (error) {
      pageUtils?.setApiErrorMsg?.(error.response?.data || getPhase3ErrorMessage(error))
    }
  }

  const selectUser = async (userId) => {
    setForm((prev) => ({ ...prev, user_id: userId, consignor_detail_id: '' }))
    const details = await fetchUserDetails(userId)

    if (details.length === 1) {
      setForm((prev) => ({ ...prev, consignor_detail_id: details[0].id?.toString() || '' }))
    }
  }

  const submit = async () => {
    if (!form.user_id) {
      pageUtils?.setAlertMsg?.('Pilih User consignor terlebih dahulu.', ALERT_TYPES.ERROR)
      return
    }

    if (userDetails.length > 0 && !form.consignor_detail_id) {
      pageUtils?.setAlertMsg?.('Pilih Consignor Detail yang akan dipakai untuk data consignor.', ALERT_TYPES.ERROR)
      return
    }

    const currentName = consignment?.consignor_detail?.formatted_name || consignment?.consignor_detail?.name || consignment?.consignor?.name || consignment?.consignor?.email || '-'
    const selectedUser = users.find((user) => user.id?.toString() === form.user_id?.toString())
    const selectedDetail = userDetails.find((detail) => detail.id?.toString() === form.consignor_detail_id?.toString())
    const nextName = selectedDetail?.formatted_name || selectedDetail?.name || selectedUser?.name || selectedUser?.email || '-'
    const confirmed = window.confirm(`Ubah consignor dari ${currentName} menjadi ${nextName}? Perubahan ini memengaruhi referensi consignor untuk proses transfer hasil penjualan.`)
    if (!confirmed) return

    setIsSaving(true)

    try {
      await InboundJourneyApiV2.updateConsignmentConsignor({ id, consignment: form, apiNamespace })
      await fetchConsignment()
      pageUtils?.setAlertMsg?.('Consignor berhasil diperbarui dan referensinya sudah disinkronkan.', ALERT_TYPES.SUCCESS)
    } catch (error) {
      pageUtils?.setApiErrorMsg?.(error.response?.data || getPhase3ErrorMessage(error))
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <Card body>
        <div className='d-flex align-items-center'>
          <Spinner size='sm' className='mr-2' />
          { 'Loading consignment...' }
        </div>
      </Card>
    )
  }

  const canEdit = !!consignment?.can_edit_consignor
  const product = consignment?.product || {}

  return (
    <>
      <div className='d-flex justify-content-between align-items-center flex-wrap mb-4' style={ { gap: 12 } }>
        <TitlePage mainTitle='Edit Consignment' subTitle={ `#${consignment?.id || '-'}` } />
        <Button tag={ Link } to='/app/all-team/consignments' color='secondary' outline>
          Back to Consignment List
        </Button>
      </div>

      <Card className='mb-4'>
        <CardBody>
          <h4>Product Data</h4>
          <Table bordered responsive>
            <tbody>
              <tr><th style={ { width: 260 } }>SKU</th><td>{ product.sku || '-' }</td></tr>
              <tr><th>Product</th><td>{ product.name || '-' }</td></tr>
              <tr><th>Internal Category</th><td>{ product.internal_category || '-' }</td></tr>
              <tr><th>Condition</th><td>{ product.condition_name || '-' }</td></tr>
              <tr><th>Comes With</th><td>{ product.comes_with || '-' }</td></tr>
              <tr><th>Batch</th><td>{ consignment?.inventory_batch_code || '-' }</td></tr>
              <tr><th>Created By</th><td>{ consignment?.created_by?.email || '-' }</td></tr>
              <tr><th>Updated</th><td>{ formatPhase3Date(consignment?.updated_at) }</td></tr>
            </tbody>
          </Table>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h4>Change Consignor</h4>
          { !canEdit ? (
            <Alert color='warning'>
              { 'Read-only access. Only owner/admin or the admin who created this consignment can edit consignor data.' }
            </Alert>
          ) : (
            <Alert color='info'>
              { 'Perubahan consignor memengaruhi referensi consignor untuk proses transfer hasil penjualan. Pastikan User dan Consignor Detail yang dipilih sudah benar.' }
            </Alert>
          ) }

          <Row form>
            <Col xs={ 12 } md={ 8 }>
              <FormGroup>
                <Label>User Search</Label>
                <div className='d-flex' style={ { gap: 8 } }>
                  <Input
                    value={ userSearch }
                    placeholder='Search user email / name / phone'
                    disabled={ !canEdit }
                    onChange={ (event) => setUserSearch(event.target.value) }
                    onKeyDown={ (event) => { if (event.key === 'Enter') searchUsers() } }
                  />
                  <Button type='button' color='primary' disabled={ !canEdit } onClick={ searchUsers }>
                    Search
                  </Button>
                </div>
              </FormGroup>
            </Col>
          </Row>

          <Row form>
            <Col xs={ 12 } md={ 6 }>
              <FormGroup>
                <Label>User</Label>
                <Input type='select' value={ form.user_id } disabled={ !canEdit } onChange={ (event) => selectUser(event.target.value) }>
                  <option value=''>Select User</option>
                  { users.map((user) => (
                    <option key={ user.id } value={ user.id }>
                      { [ readPhase3Text(user.name) || readPhase3Text(user.email), `#${user.id}`, readPhase3Text(user.email) ].filter(Boolean).join(' — ') }
                    </option>
                  )) }
                </Input>
              </FormGroup>
            </Col>
            <Col xs={ 12 } md={ 6 }>
              <FormGroup>
                <Label>Consignor Detail</Label>
                <Input type='select' value={ form.consignor_detail_id } disabled={ !canEdit } onChange={ (event) => setForm((prev) => ({ ...prev, consignor_detail_id: event.target.value })) }>
                  <option value=''>Select Consignor Detail</option>
                  { userDetails.map((detail) => (
                    <option key={ detail.id } value={ detail.id }>
                      { [ readPhase3Text(detail.formatted_name) || readPhase3Text(detail.name), `Detail #${detail.id}` ].filter(Boolean).join(' — ') }
                    </option>
                  )) }
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <Button type='button' color='success' disabled={ !canEdit || isSaving || !form.user_id || (userDetails.length > 0 && !form.consignor_detail_id) } onClick={ submit }>
            { isSaving ? 'Saving...' : 'Save Consignor' }
          </Button>
        </CardBody>
      </Card>
    </>
  )
}

export default Phase3ConsignmentEditPage
